import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import {
  getInvoices,
  getInvoiceAnalytics,
  deleteInvoice,
  sendInvoice
} from '../../services/invoiceService';
import InvoiceStatsCards from './components/InvoiceStatsCards';
import InvoiceTable from './components/InvoiceTable';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import PaymentModal from './components/PaymentModal';
import InvoiceDetailModal from './components/InvoiceDetailModal';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partial' },
  { value: 'overdue', label: 'Overdue' }
];

const AGING_FILTERS = [
  { value: 'all', label: 'All Ages' },
  { value: '0-30', label: '0-30 days' },
  { value: '31-60', label: '31-60 days' },
  { value: '60+', label: '60+ days' }
];

const InvoiceManagement = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [agingFilter, setAgingFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getInvoices({
        status: statusFilter,
        dateFrom,
        dateTo,
        search
      });
      // Apply aging filter client-side
      const filtered = agingFilter === 'all'
        ? data
        : data?.filter(inv => inv?.agingCategory === agingFilter);
      setInvoices(filtered);
    } catch (err) {
      setError(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [user?.id, statusFilter, agingFilter, dateFrom, dateTo, search]);

  const fetchAnalytics = useCallback(async () => {
    if (!user?.id) return;
    try {
      setAnalyticsLoading(true);
      const data = await getInvoiceAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInvoices();
    fetchAnalytics();
  }, [fetchInvoices, fetchAnalytics]);

  const showSuccessMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleView = (invoice) => {
    setDetailInvoiceId(invoice?.id);
    setShowDetail(true);
  };

  const handleEdit = (invoice) => {
    setDetailInvoiceId(invoice?.id);
    setShowDetail(true);
  };

  const handleSend = async (invoice) => {
    try {
      await sendInvoice(invoice?.id);
      showSuccessMessage(`Invoice ${invoice?.invoiceNumber} sent and locked`);
      fetchInvoices();
      fetchAnalytics();
    } catch (err) {
      setError(err?.message || 'Failed to send invoice');
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice?.invoiceNumber}? This cannot be undone.`)) return;
    try {
      await deleteInvoice(invoice?.id);
      showSuccessMessage('Invoice deleted');
      fetchInvoices();
      fetchAnalytics();
    } catch (err) {
      setError(err?.message || 'Failed to delete invoice');
    }
  };

  const handleRecordPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPayment(true);
  };

  const handlePaymentRecorded = () => {
    showSuccessMessage('Payment recorded successfully');
    fetchInvoices();
    fetchAnalytics();
  };

  const handleInvoiceCreated = () => {
    showSuccessMessage('Invoice created successfully');
    fetchInvoices();
    fetchAnalytics();
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setAgingFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const hasActiveFilters = statusFilter !== 'all' || agingFilter !== 'all' || dateFrom || dateTo || search;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto md:ml-sidebar">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading-semibold text-text-primary">Invoice Management</h1>
              <p className="text-sm text-text-secondary mt-0.5">Billing, payments, and financial tracking</p>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Create Invoice</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Alerts */}
          {success && (
            <div className="mb-4 flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{success}</span>
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
            </div>
          )}

          {/* Stats */}
          <InvoiceStatsCards analytics={analytics} loading={analyticsLoading} />

          {/* Filters */}
          <div className="bg-surface rounded-xl border border-border p-4 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  placeholder="Search invoice # or case..."
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Status Filter */}
              <div className="flex gap-1 bg-secondary-50 p-1 rounded-lg">
                {STATUS_FILTERS?.map(f => (
                  <button
                    key={f?.value}
                    onClick={() => setStatusFilter(f?.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-body-medium transition-colors ${
                      statusFilter === f?.value
                        ? 'bg-surface text-primary shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {f?.label}
                  </button>
                ))}
              </div>

              {/* Aging Filter */}
              <select
                value={agingFilter}
                onChange={e => setAgingFilter(e?.target?.value)}
                className="border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {AGING_FILTERS?.map(f => (
                  <option key={f?.value} value={f?.value}>{f?.label}</option>
                ))}
              </select>

              {/* Date Range */}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-text-secondary text-xs">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  <Icon name="X" size={13} />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Invoice Count */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-text-secondary">
              {loading ? 'Loading...' : `${invoices?.length} invoice${invoices?.length !== 1 ? 's' : ''} found`}
            </p>
          </div>

          {/* Table */}
          <InvoiceTable
            invoices={invoices}
            loading={loading}
            onView={handleView}
            onEdit={handleEdit}
            onSend={handleSend}
            onDelete={handleDelete}
            onRecordPayment={handleRecordPayment}
          />
        </div>
      </main>
      {/* Modals */}
      {showCreate && (
        <CreateInvoiceModal
          onClose={() => setShowCreate(false)}
          onCreated={handleInvoiceCreated}
        />
      )}
      {showPayment && selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={() => { setShowPayment(false); setSelectedInvoice(null); }}
          onPaymentRecorded={handlePaymentRecorded}
        />
      )}
      {showDetail && detailInvoiceId && (
        <InvoiceDetailModal
          invoiceId={detailInvoiceId}
          onClose={() => { setShowDetail(false); setDetailInvoiceId(null); }}
          onUpdated={() => { fetchInvoices(); fetchAnalytics(); }}
        />
      )}
    </div>
  );
};

export default InvoiceManagement;
